import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info, RefreshCw } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import {
    Card,
    CardAction,
    CardContent,
    CardCount,
    CardHeader,
    CardTitle,
} from '@/components/Card/Card';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover/Popover';
import { FilterSelect } from '@/components/Select/FilterSelect';
import {
    SegmentedControl,
    SegmentedControlItem,
} from '@/components/SegmentedControl/SegmentedControl';
import { type Metric } from '@/features/reports/api';
import { APP_USAGE_COPY, CLIENTS_COPY, REPORTS_COPY } from '@/features/reports/copy';
import { formatChange } from '@/features/reports/format';
import { REPORT_PERIODS, type ReportPeriod } from '@/features/reports/periods';
import { revealChoice } from '@/features/reports/reveal';
import { APP_USAGE_WINDOWS, type AppUsageDays } from '@/features/reports/tabs';
import { relationshipManagersQuery } from '@/features/users/queries';
import { cn } from '@/util/utils';

/*
  The pieces every report tab draws its figures with (ALS-85, shared with Transactions in
  ALS-86): a titled card, a tile with its change against the period before, and the one failure
  state a whole tab shows when its request fails.
*/

export function ReportCard({
    title,
    subtitle,
    actions,
    actionsClassName,
    children,
}: {
    title: string;
    subtitle: string;
    actions?: ReactNode;
    /** For actions that take a row of their own on a phone, such as a picker. */
    actionsClassName?: string;
    children: ReactNode;
}) {
    return (
        <Card floating className="min-w-0">
            <CardHeader>
                <CardTitle>{title}</CardTitle>
                <CardCount>{subtitle}</CardCount>
                {actions ? <CardAction className={actionsClassName}>{actions}</CardAction> : null}
            </CardHeader>
            <CardContent>{children}</CardContent>
        </Card>
    );
}

/**
 * The period control Overview and Transactions share. Five choices fit a 320 phone once their
 * sides close in below 360, so none is cut off mid-word; past that the control still scrolls
 * inside itself, and a choice at its far end is brought into view.
 */
export function PeriodControl({
    period,
    onPeriodChange,
    periods = REPORT_PERIODS,
    label = REPORTS_COPY.period,
}: {
    period: ReportPeriod;
    onPeriodChange: (period: ReportPeriod) => void;
    /** The choices, in order: Tax offers only the cycles VAT is filed on (ALS-90). */
    periods?: readonly ReportPeriod[];
    label?: string;
}) {
    return (
        <SegmentedControl
            tone="solid"
            aria-label={label}
            value={period}
            onValueChange={(next) => onPeriodChange(next as ReportPeriod)}
            className="max-w-full overflow-x-auto"
        >
            {periods.map((p) => (
                <SegmentedControlItem
                    key={p}
                    value={p}
                    onClick={revealChoice}
                    className="max-[359px]:px-2 pointer-coarse:min-w-11"
                >
                    {p}
                </SegmentedControlItem>
            ))}
        </SegmentedControl>
    );
}

/**
 * App usage's window (ALS-160): 7, 30 or 90 days, the same segment control as the period's. Three
 * short choices fit a 320 phone whole, so nothing here scrolls.
 */
export function WindowControl({
    days,
    onDaysChange,
}: {
    days: AppUsageDays;
    onDaysChange: (days: AppUsageDays) => void;
}) {
    return (
        <SegmentedControl
            tone="solid"
            aria-label={APP_USAGE_COPY.window}
            value={String(days)}
            onValueChange={(next) => onDaysChange(Number(next) as AppUsageDays)}
        >
            {APP_USAGE_WINDOWS.map((d) => (
                <SegmentedControlItem key={d} value={String(d)} className="pointer-coarse:min-w-11">
                    {APP_USAGE_COPY.days(d)}
                </SegmentedControlItem>
            ))}
        </SegmentedControl>
    );
}

const EVERYONE = 'all';

/**
 * The page's relationship manager, as the prototype's header filter is: one choice that scopes
 * Clients (ALS-87) and App usage (ALS-160) alike, carried in the address. A manager in the address
 * who is no longer one still scopes the report, and is named by id.
 */
export function ManagerFilter({
    rmId,
    onManagerChange,
}: {
    rmId: number | undefined;
    onManagerChange: (rmId: number | undefined) => void;
}) {
    const managers = useQuery(relationshipManagersQuery);
    const options = [
        { value: EVERYONE, label: CLIENTS_COPY.rm.all },
        ...(managers.data ?? []).map((m) => ({ value: String(m.id), label: m.name })),
    ];
    const value = rmId === undefined ? EVERYONE : String(rmId);
    if (!options.some((o) => o.value === value)) {
        options.push({ value, label: `${CLIENTS_COPY.rm.label} ${value}` });
    }
    return (
        <FilterSelect
            label={CLIENTS_COPY.rm.label}
            value={value}
            options={options}
            onChange={(next) => onManagerChange(next === EVERYONE ? undefined : Number(next))}
            className="w-full sm:w-[240px]"
        />
    );
}

/** One failure for the whole tab, with a retry: half a report is not a state it can be in. */
export function ReportFailure({
    copy,
    onRetry,
}: {
    copy: { title: string; body: string; retry: string };
    onRetry: () => void;
}) {
    return (
        <Card floating>
            <EmptyState
                tone="destructive"
                icon={<RefreshCw />}
                title={copy.title}
                body={copy.body}
                actions={
                    <Button variant="outline" size="sm" onClick={onRetry}>
                        {copy.retry}
                    </Button>
                }
            />
        </Card>
    );
}

/**
 * .rp-kpi: a label, the figure, and its change against the period before. The arrow always
 * follows the figure; the colour follows whether that is good news, so a figure where less is
 * better (refunds) passes `lowerIsBetter` and turns red as it rises. A `note` says what the
 * figure is net of, such as the Overview's margin after refunds. Brand intelligence (ALS-91) adds
 * a `definition` behind an (i), and `changeText` for words in place of the change: why the figure
 * is missing, or that there is no prior quarter.
 */
export function KpiTile({
    label,
    metric,
    format,
    unit = '%',
    lowerIsBetter = false,
    note,
    definition,
    changeText,
}: {
    label: string;
    metric: Metric;
    format: (value: number) => string;
    unit?: '%' | 'pt';
    lowerIsBetter?: boolean;
    note?: string;
    definition?: string;
    changeText?: string;
}) {
    const change =
        changeText === undefined
            ? formatChange(metric.change, unit, metric.gap)
            : { text: changeText, tone: 'flat' as const };
    const good = lowerIsBetter ? 'down' : 'up';
    const bad = lowerIsBetter ? 'up' : 'down';
    return (
        <div
            role="group"
            aria-label={label}
            className="bg-card border-border min-w-0 rounded-lg border px-4 py-3"
        >
            <p className="text-label-md text-muted-foreground flex items-center gap-1.5">
                {label}
                {definition ? <Definition label={label}>{definition}</Definition> : null}
            </p>
            <p data-slot="tile-value" className="mt-1 text-2xl font-semibold tracking-tight">
                {metric.value === null ? '–' : format(metric.value)}
            </p>
            <p
                data-slot="tile-change"
                className={cn(
                    'mt-1 text-xs font-medium',
                    change.tone === good && 'text-success',
                    change.tone === bad && 'text-destructive',
                    change.tone === 'flat' && 'text-muted-foreground',
                )}
            >
                {change.tone === 'up' ? '▲ ' : change.tone === 'down' ? '▼ ' : ''}
                {change.text}
            </p>
            {note ? (
                <p data-slot="tile-note" className="text-muted-foreground mt-0.5 text-xs">
                    {note}
                </p>
            ) : null}
        </div>
    );
}

/**
 * The prototype's ".rp-info" beside a figure's label (ALS-87): what the figure means, because
 * "at risk" or "repeat rate" mean nothing without one. A button opening a popover rather than a
 * hover tip, so a phone and a keyboard reach it too.
 */
export function Definition({ label, children }: { label: string; children: ReactNode }) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label={REPORTS_COPY.definition(label)}
                    className="text-muted-foreground hover:text-foreground focus-visible:outline-ring relative inline-flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full align-text-bottom outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid pointer-coarse:after:absolute pointer-coarse:after:-inset-3.5 pointer-coarse:after:content-[''] [&>svg]:size-3.5"
                >
                    <Info aria-hidden />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                aria-label={label}
                className="w-72 max-w-[calc(100vw-2rem)] text-sm"
            >
                <p className="font-medium">{label}</p>
                <p className="text-muted-foreground mt-1">{children}</p>
            </PopoverContent>
        </Popover>
    );
}

export function Tile({
    label,
    value,
    note,
    definition,
}: {
    label: string;
    value: string;
    note: string;
    definition?: string;
}) {
    return (
        <div
            role="group"
            aria-label={label}
            className="bg-card border-border min-w-0 rounded-lg border px-4 py-3"
        >
            <p className="text-label-md text-muted-foreground flex items-center gap-1.5">
                {label}
                {definition ? <Definition label={label}>{definition}</Definition> : null}
            </p>
            <p data-slot="tile-value" className="mt-1 text-2xl font-semibold tracking-tight">
                {value}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">{note}</p>
        </div>
    );
}
